import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { PrimaryButton } from "components/Button/PrimaryButton";

import { isTimeoutError } from "./api";
import { AIHazardShell } from "./types";
import { ReasoningPanel } from "./ReasoningPanel";
import { GenerationLoadingView } from "./GenerationLoadingView";
import { useUpdateAIHazard } from "./hooks/useUpdateAIHazard";
import { useGenerateAIHazardDetail } from "./hooks/useGenerateAIHazardDetail";

// Stage-2 per-hazard loading phases (plan §11).
export const GENERATE_PHASE_KEYS = [
  "risks.ai.detailLoading.classifying",
  "risks.ai.detailLoading.assessing",
  "risks.ai.detailLoading.mitigating",
  "risks.ai.detailLoading.verifying",
];

interface HazardShellFormProps {
  projectId: string;
  hazard: AIHazardShell;
}

// PHASE A (plan §1): edit the stage-1 shell (name / description / per-hazard
// user context) with plain controlled inputs — deliberately NOT the risk Form
// — and kick off the full stage-2 generation. The generate request carries
// the FRESHEST input values so unsaved edits are honored (contract endpoint
// 4); the inputs are local state, so edits also survive a failed generation.
export const HazardShellForm = ({ projectId, hazard }: HazardShellFormProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState(hazard.name);
  const [description, setDescription] = useState(hazard.description);
  const [userContext, setUserContext] = useState(hazard.user_context || "");

  const updateMutation = useUpdateAIHazard(projectId);
  const generateMutation = useGenerateAIHazardDetail(projectId);

  const isGenerating = generateMutation.isLoading;
  const isBusy = isGenerating || updateMutation.isLoading;
  const hasRequiredValues = !!name.trim() && !!description.trim();

  const handleSave = () => {
    if (isBusy) return;

    updateMutation.mutate({
      hazardId: hazard.id,
      patch: {
        name: name.trim(),
        description: description.trim(),
        user_context: userContext.trim(),
      },
    });
  };

  const handleGenerate = () => {
    if (isBusy || !hasRequiredValues) return;

    // Shell values are sent inline — no separate save required; the server
    // persists them onto the shell. On success the hook merges the returned
    // shell (draft persisted, status → generated) into the session cache and
    // the detail page flips to Phase B.
    generateMutation.mutate({
      hazardId: hazard.id,
      mode: "initial",
      name: name.trim(),
      description: description.trim(),
      user_context: userContext.trim(),
    });
  };

  return (
    <div className="mt-6 space-y-4 max-w-3xl">
      {/* AI reasoning is always visible in Phase A (plan §1). */}
      <ReasoningPanel
        headlineKey="risks.ai.shell.reasoning.headline"
        reasoning={hazard.reasoning}
      />

      {isGenerating && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-30">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-sm mx-4">
            <GenerationLoadingView phaseKeys={GENERATE_PHASE_KEYS} />
          </div>
        </div>
      )}

      <div>
        <label
          htmlFor="ai-hazard-name"
          className="block text-sm font-medium text-gray-700"
        >
          {t("risks.ai.shell.name.label")}
        </label>
        <input
          id="ai-hazard-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={isGenerating}
          className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        />
      </div>

      <div>
        <label
          htmlFor="ai-hazard-description"
          className="block text-sm font-medium text-gray-700"
        >
          {t("risks.ai.shell.description.label")}
        </label>
        <textarea
          id="ai-hazard-description"
          rows={4}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={isGenerating}
          className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        />
      </div>

      <div>
        <label
          htmlFor="ai-hazard-user-context"
          className="block text-sm font-medium text-gray-700"
        >
          {t("risks.ai.shell.userContext.label")}
        </label>
        <textarea
          id="ai-hazard-user-context"
          rows={3}
          value={userContext}
          onChange={(event) => setUserContext(event.target.value)}
          disabled={isGenerating}
          placeholder={t("risks.ai.shell.userContext.placeholder")}
          className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        />
      </div>

      {generateMutation.isError && !isGenerating && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            {t(
              isTimeoutError(generateMutation.error)
                ? "risks.ai.errors.timeout"
                : "risks.ai.errors.generationFailed"
            )}
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={isBusy}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
        >
          {t("risks.ai.shell.save.cta")}
        </button>
        <PrimaryButton
          testId="ai-hazard-generate"
          onClick={handleGenerate}
          disabled={isBusy || !hasRequiredValues}
        >
          {t("risks.ai.shell.generate.cta")}
        </PrimaryButton>
      </div>
    </div>
  );
};
